if (!customElements.get('inkybox-volume-discount')) {
  customElements.define(
    'inkybox-volume-discount',
    class InkyboxVolumeDiscount extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info') || this.closest('.shopify-section') || document;
        this.panel = this.querySelector('[data-volume-discount-panel]');
        this.priceData = this.readPriceData();
        this.handleClick = this.handleClick.bind(this);
        this.handleProductChange = this.handleProductChange.bind(this);

        this.panel?.addEventListener('click', this.handleClick);
        this.productInfo.addEventListener('click', this.handleProductChange, true);
        this.productInfo.addEventListener('input', this.handleProductChange, true);
        this.productInfo.addEventListener('change', this.handleProductChange, true);

        this.placementObserver = new MutationObserver(() => this.schedulePlacement());
        this.placementObserver.observe(this.productInfo, { childList: true, subtree: true });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data || String(data.sectionId) === String(this.dataset.sectionId)) this.refresh();
          });
          this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.refresh());
        }

        this.schedulePlacement();
        this.refresh();
      }

      disconnectedCallback() {
        this.panel?.removeEventListener('click', this.handleClick);
        this.productInfo?.removeEventListener('click', this.handleProductChange, true);
        this.productInfo?.removeEventListener('input', this.handleProductChange, true);
        this.productInfo?.removeEventListener('change', this.handleProductChange, true);
        this.placementObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
        if (this.placementFrame) cancelAnimationFrame(this.placementFrame);
        if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
      }

      readPriceData() {
        try {
          return JSON.parse(this.querySelector('[data-volume-variant-prices]')?.textContent || '{}');
        } catch (error) {
          console.warn('Inkybox volume discount prices could not be read.', error);
          return {};
        }
      }

      get quantityInput() {
        const candidates = this.productInfo.querySelectorAll(
          '#personalized-form .cart-qty select, quantity-input input[name="quantity"], .quantity__input[name="quantity"], select[name="quantity"], input[name="quantity"]'
        );

        return Array.from(candidates).find((input) => {
          const bounds = input.getBoundingClientRect();
          return bounds.width > 0 && bounds.height > 0;
        }) || candidates[0];
      }

      get variantId() {
        return (
          this.productInfo.querySelector('product-form form [name="id"]')?.value ||
          this.productInfo.querySelector('form[data-type="add-to-cart-form"] [name="id"]')?.value ||
          this.dataset.initialVariantId
        );
      }

      handleClick(event) {
        const tier = event.target.closest('[data-volume-quantity]');
        if (!tier || !this.panel?.contains(tier)) return;

        const input = this.quantityInput;
        if (!input) return;

        const requestedQuantity = Number(tier.dataset.volumeQuantity);
        const minimum = Number(input.min || input.dataset.min || 1);
        const maximum = input.max ? Number(input.max) : null;
        const step = Number(input.step || 1);
        let quantity = Math.max(requestedQuantity, minimum);

        if (step > 1) quantity = minimum + Math.ceil((quantity - minimum) / step) * step;
        if (Number.isFinite(maximum)) quantity = Math.min(quantity, maximum);

        input.value = quantity;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        this.refresh();
      }

      handleProductChange(event) {
        const customallVariantChoice =
          event.type === 'click' && event.target.closest?.('#personalized-form button[data-ctm-value]');

        if (
          customallVariantChoice ||
          event.target === this.quantityInput ||
          event.target.matches('#personalized-form .cart-qty select, [name="quantity"], [name="id"]')
        ) {
          this.refresh();
        }
      }

      refresh() {
        if (this.refreshFrame) return;

        this.refreshFrame = requestAnimationFrame(() => {
          this.refreshFrame = null;
          this.updatePrices();
          this.updateSelectedTier();
        });
      }

      schedulePlacement() {
        if (this.placementFrame || !this.panel?.isConnected) return;

        this.placementFrame = requestAnimationFrame(() => {
          this.placementFrame = null;
          this.placePanel();
        });
      }

      placePanel() {
        const customallPurchaseRow = this.productInfo.querySelector('#personalized-form .add-to-cart-group');
        const nativePurchaseRow = this.productInfo.querySelector('.qbb-row');
        const nativeButtons = this.productInfo.querySelector('.product-form__buttons');
        const isPersonalizedProduct = this.productInfo.classList?.contains('inkybox-personalized-product');
        const target = isPersonalizedProduct
          ? customallPurchaseRow
          : nativePurchaseRow || nativeButtons;

        if (!target?.parentNode || target.previousElementSibling === this.panel) return;

        target.parentNode.insertBefore(this.panel, target);
        this.placementObserver?.disconnect();
        this.refresh();
      }

      updatePrices() {
        const prices = this.priceData[String(this.variantId)];
        if (!prices) return;

        this.panel?.querySelectorAll('[data-volume-price]').forEach((priceElement) => {
          const formattedPrice = prices[priceElement.dataset.volumePrice];
          if (!formattedPrice) return;
          priceElement.textContent = formattedPrice;
        });

        this.panel?.querySelectorAll('[data-volume-original-price]').forEach((priceElement) => {
          priceElement.textContent = prices.original;
        });
      }

      updateSelectedTier() {
        const quantity = Number(this.quantityInput?.value || 1);

        this.panel?.querySelectorAll('[data-volume-quantity]').forEach((tier) => {
          const threshold = Number(tier.dataset.volumeQuantity);
          const isSelected = threshold === 4 ? quantity >= 4 : quantity === threshold;
          tier.setAttribute('aria-pressed', String(isSelected));
        });
      }
    }
  );
}
